<template>
  <div class="creator-visits-page">
    <Loader v-if="isLoading" style="position: absolute"></Loader>

    <template v-else>
      <div class="global-h1 creator-visits-page__h1">
        {{ hasACollabSubscription ? "Creator Visits" : "Gain access to Creator Visits!" }}
      </div>

      <div v-if="hasACollabSubscription" class="creator-visits-page__quick-links">
        <LinkWithIcon tag="router-link" :to="{ name: 'VisitInspiration', params: { pageId: communityId } }" color="teal">
          <template #icon>
            <IconEmbedded name="bulb_2" :size="21" />
          </template>
          <span>Visit Inspiration</span>
        </LinkWithIcon>
        <LinkWithIcon tag="router-link" :to="{ name: 'IndustryInsights', params: { pageId: communityId } }" color="teal">
          <template #icon>
            <IconEmbedded name="binoculars_2" :size="21" />
          </template>
          <span>Visit Examples</span>
        </LinkWithIcon>
      </div>

      <!-- planned collabs -->
      <PlannedCollabsList
        v-if="collabsRemaining > 0 && !plannedCollabsState.loading && collabs && customerDetails?.isDisabled !== true"
        :customerId="customerDetails.id"
        :plannedCollabs="plannedCollabsState.plannedCollabs"
        :expectedPlannedCollabsCount="collabsRemaining"
        :createdCollabCount="collabs.length"
        @click:getStarted="startCollabFromPlannedCollab"
        @savePlannedCollabs="savePlannedCollabs"
      />

      <!-- Destination Info -->
      <div class="spoiler-section creator-visits-page__spoiler-section" style="margin-bottom: 35px">
        <SrpDetailsSummary class="spoiler-section__details-summary" :isOpened="false" :transitionDuration="120">
          <template #heading="{ isOpened }">
            <div
              :class="{
                'global-h3': true,
                'spoiler-section__summary': true,
                'spoiler-section__summary--with-bottom-padding': !isOpened,
              }"
            >
              <span><span>Destination Info</span></span>
            </div>
          </template>
          <template #details>
            <div class="spoiler-section__content">
              <CommunityBoundaryGuidanceReadonly :communityId="communityId" hideExpandCollapse hideBorder showEditButton />
            </div>
          </template>
        </SrpDetailsSummary>
      </div>

      <!-- Header -->
      <div class="header creator-visits-page__header">
        <div class="header__left-side">
          <!--<VideoIntroSnippet
            class="header__video-snippet"
            thumbnail="https://cdn.shrpa.com/videos/howto/thumb/collab-intro.jpg"
            video-url="https://cdn.shrpa.com/videos/howto/collab-intro.mp4"
          />-->
          <div v-if="!hasACollabSubscription" class="header__description">
            <div>
              Creator Visits are the most effective way to create high-quality, targeted UGC.<br />
              Your area has outstanding creators ready to explore your destination and deliver exceptional content.<br />
            </div>
            <SrpButton color="orange" size="tiny" style="margin-top: 13px" @click="askToBuyCollab(true)"> Want Creator Visits! </SrpButton>
            <a href="https://explore.shrpa.com/collaborate-with-content-creators/" target="_blank" style="margin-left: 20px; text-decoration: underline">Learn more</a>
          </div>

          <div v-if="hasACollabSubscription && collabs" style="margin-left: -25px">
            <!-- Collab select dropdown -->
            <div v-if="collabs.length > 0" class="collab-select-dropdown creator-visits-page__collab-select-dropdown">
              <div class="global-h2 collab-select-dropdown__overlay">
                <div class="collab-select-dropdown__overlay-text">{{ getCollabNameText(collabs[selectedCollabIndex]) }}</div>
              </div>
              <select class="collab-select-dropdown__select-itself" v-model="selectedCollabIndex" @change="collabSelectionChanged">
                <option v-for="item in collabs.map((c, index) => ({ text: getCollabNameText(c), value: index }))" :key="item.value" :value="item.value">{{ item.text }}</option>
              </select>
              <IconEmbedded class="collab-select-dropdown__caret-icon" name="caret-bottom_3-5" :size="22" />
            </div>
            <!-- / Collab select dropdown -->
            <div v-else>
              <h2 style="margin-bottom: 3px">Wondering where to start?</h2>
              <div style="margin-bottom: 8px">Explore ideas inspired by past successful creator visits.</div>
              <SrpButton :to="{ name: 'VisitInspiration', params: { pageId: communityId } }" class="header__setup-btn" size="small">
                <template #icon>
                  <IconEmbedded name="ai_2" :size="18" />
                </template>
                Get Visit Inspiration
              </SrpButton>
            </div>
          </div>
        </div>

        <div v-if="hasACollabSubscription && customerDetails?.isDisabled !== true && customerDetails?.isSpotlight !== true" class="header__right-side">
          <SrpButton class="header__setup-btn" @click="newCollabClicked" size="small" color="teal" fill="outlined">
            <template #icon>
              <IconEmbedded name="user-plus_2-5" :size="18" />
            </template>
            {{ collabs.length > 0 ? "Set up another Creator Visit" : "Start a Creator Visit" }}
          </SrpButton>

          <!-- Stats list -->
          <ul v-if="collabsRemaining > 0" class="stats-list header__stats-list">
            <li class="stats-list__line">
              <b class="stats-list__number">{{ collabsRemaining }}</b> {{ collabsTerm }} Remaining
            </li>
            <!--<li class="stats-list__line">
                <b class="stats-list__number">{{ collabsRemaining * 4 }}</b> Adventures
              </li>-->
          </ul>
          <!-- / Stats list -->

          <SrpButton v-else class="header__setup-btn" @click="isAddMoreCreatorVisitsModalVisible = true" size="small" fill="outlined">
            <template #icon>
              <IconEmbedded name="plus_4" :size="18" />
            </template>
            Add More Creator Visits
          </SrpButton>

          <!--<template v-else>
            <SrpButton v-if="collabs.length > 0" @click="askToBuyCollab" class="header__setup-btn" size="small" color="orange"> Want more Creator Visits? </SrpButton>
          </template>-->
        </div>
      </div>
      <!-- / Header -->

      <AddMoreCreatorVisitsModal
        v-model:isVisible="isAddMoreCreatorVisitsModalVisible"
        :communityId="communityId"
        @purchase="
          () => {
            loadCustomerDetails($route.params.communityId as string);
            loadCollabs($route.params.communityId as string);
          }
        "
      />

      <template v-if="hasACollabSubscription && collabs">
        <!--<Preview />-->
        <!--<router-link :to="{name: 'CommunityAdminDashboard'}" lang="en" hreflang="en" class="ui small basic button right floated">Back to Dashboard</router-link>-->

        <template v-if="communityId && selectedCollab">
          <!-- CollabSummary always shows now that we have the collab wizard -->
          <CollabSummary
            class="creator-visits-page__spoiler-section"
            :communityId="communityId"
            v-model:collabInput="selectedCollab"
            :collabRemainingCounts="collabRemainingCounts"
            :collabsRemaining="collabsRemaining"
            :customerIsDisabled="customerDetails?.isDisabled"
            :collabIndex="selectedCollabIndex"
            :canEditInputs="canEditCollabInputs"
            :currentDateLocal="currentDateLocal"
            :communityCreatorMatchSummary="communityCreatorMatchSummary"
            :isImpersonating="isImpersonating"
            :invitedCreators="invitedCreatorsForCurrentCollab"
            :isDestinationContactEditable="!selectedCollab?.collaboration?.completedDate"
            @needMoreCollabs="askToBuyCollab"
            @deleted="currentCollabDeleted"
            @scroll-to-creators="$refs?.creatorsSection?.$el?.scrollIntoView({ behavior: 'smooth' })"
            @loadCreatorDetails="_creatorDetails => (creatorDetails = _creatorDetails)"
            ref="collabSummary"
            @editCurrentCollab="editCurrentCollab"
            @editDestinationContact="openDestinationContactModal"
          />

          <CreatorInvites
            class="creator-visits-page__spoiler-section"
            v-if="isCreatorInvitesVisible && selectedCollab.version >= 2 && !selectedCollab.shrpaIsChoosingCreators"
            :reloadTrigger="reloadCollabCreatorInputs"
            :communityId="communityId"
            :collabInput="selectedCollab"
            :collabIndex="selectedCollabIndex"
            :collabsRemaining="collabsRemaining"
            @selectMultipleCreators="loadCollabs"
            @invitedCreatorsLoaded="data => (invitedCreatorsForCurrentCollab = data)"
            ref="creatorsSection"
          />

          <CommunityCorrections
            v-if="selectedCollab?.collaboration?.creatorId"
            class="creator-visits-page__spoiler-section"
            :communityId="communityId"
            :collabInput="selectedCollab"
            :collabIndex="selectedCollabIndex"
            :creatorId="selectedCollab?.collaboration?.creatorId"
            :forceLocationsView="isCollabContentUsageVisible"
            @updated="collabUpdate"
            @collabMarkedComplete="scrollToFeedback"
          />

          <CollabFeedbackComponent
            ref="feedbackSection"
            v-if="selectedCollab.feedback"
            class="creator-visits-page__spoiler-section"
            :communityId="communityId"
            :collabInputId="selectedCollab.id"
            :collabIndex="selectedCollabIndex"
            :feedback="selectedCollab.feedback"
            @updated="feedbackUpdate"
            @feedbackSubmitted="scrollToContentUsage"
            :creatorDetails="creatorDetails"
          />

          <CollabContentUsage
            ref="contentUsageSection"
            v-if="isCollabContentUsageVisible && selectedCollab"
            class="creator-visits-page__spoiler-section"
            :communityId="communityId"
            :collabInput="selectedCollab"
            :collabIndex="selectedCollabIndex"
            :enableGenAdventures="enableGenAdventures"
          />
        </template>

        <div v-if="collabs.length > 0" class="creator-visits-page__admin-buttons-section">
          <SuperUserActionPanel>
            <div>
              <router-link
                v-if="selectedCollab"
                :to="{ name: 'SuperUserCollabDetails', params: { communityId: communityId, collabInputId: selectedCollab.id } }"
                target="_blank"
                style="margin-right: 12px"
              >
                <SrpButton color="gray" fill="outlined" size="tiny" style="opacity: 0.5">Backend View</SrpButton>
              </router-link>

              <router-link
                v-if="selectedCollab && creatorDetails"
                :to="{ name: 'CollabOpportunity', params: { creatorId: creatorDetails.sherpaId, communityId: communityId, collabInputId: selectedCollab.id } }"
                target="_blank"
                style="margin-right: 12px"
              >
                <SrpButton color="gray" fill="outlined" size="tiny" style="opacity: 0.5">Creator View</SrpButton>
              </router-link>

              <SrpButton v-if="isCreatorInvitesVisible" @click="$refs.creatorsSection?.showRankCreators()" color="gray" fill="outlined" size="tiny" style="margin-right: 12px; opacity: 0.5"
                >Rank Creators</SrpButton
              >
            </div>

            <div style="margin-top: 12px">
              <SrpButton
                v-if="!isCollabContentUsageVisible && selectedCollab"
                @click="forceContentUsageVisible = true"
                color="gray"
                fill="outlined"
                size="tiny"
                style="margin-right: 12px; opacity: 0.5"
              >
                Content Uses
              </SrpButton>
              <SrpButton v-else-if="!enableGenAdventures" @click="enableGenAdventures = true" color="gray" fill="outlined" size="tiny" style="margin-right: 12px; opacity: 0.5"> Adv Gen </SrpButton>
              <SrpButton @click="showDateOverrideModal = true" color="gray" fill="outlined" size="tiny" style="margin-right: 12px; opacity: 0.5"> Simulate Date </SrpButton>
              <SrpButton @click="askToDelete" color="gray" fill="outlined" size="tiny" style="margin-right: 12px; opacity: 0.5">Hard Delete</SrpButton>
            </div>

            <div style="margin-top: 10px">
              <MessagingImpersonateCheckbox v-model:isImpersonating="isImpersonating" />
            </div>
          </SuperUserActionPanel>
        </div>
      </template>
    </template>
  </div>

  <!--New Collab Wizard-->
  <CreateCollabModal
    v-if="showCreateOrEditModal"
    :communityId="communityId"
    :collabSuggestionId="collabSuggestionId"
    :defaultTheme="defaultTheme"
    :collabRemainingCounts="collabRemainingCounts"
    :createdForCreatorId="createdForCreatorId"
    :collabInput="collabInputForCreateOrEditFlow"
    :visible="showCreateOrEditModal"
    :plannedCollab="plannedCollabForCreateModal"
    @collabCreated="collabCreated"
    @close="
      async () => {
        cancelCreate();
        refreshCreatorDetails();
      }
    "
    @setCollabSuggestionId="
      newId => {
        collabSuggestionId = newId;
      }
    "
    @editCollabDraft="
      collabId => {
        selectedCollabIndex = collabs.findIndex(c => c.id === collabId);
        editCurrentCollab();
      }
    "
    ref="createCollabModal"
    :collabs="collabs"
    :plannedCollabs="plannedCollabsState.plannedCollabs"
    :collabSummaries="collabSummaries"
    :isLoadingCollabSummaries="isLoadingCollabSummaries"
    :isLoadingPlannedCollabs="plannedCollabsState.loading"
  />

  <!--Delete Modal-->
  <SrpModal v-model:isVisible="showDeleteModal" size="small">
    <template #header>
      <h2 class="global-h2">You sure???<br />This is a superuser hard delete operation</h2>
    </template>
    <template #footer>
      <SrpButton @click="cancelDelete" color="gray" fill="outlined" style="margin-right: 10px" :isDisabled="isDeletingCollab">Cancel</SrpButton>
      <SrpButton
        @click="
          async () => {
            await deleteCollab();
            await $refs.collabSummary.load();
          }
        "
        color="orange"
        :isDisabled="isDeletingCollab"
      >
        <template v-if="isDeletingCollab" #icon>
          <LoadingIcon :size="16" />
        </template>
        Yep, delete it
      </SrpButton>
    </template>
  </SrpModal>

  <!--Purchase Modal-->
  <SrpModal v-model:isVisible="showPurchaseModal" :isClosable="false">
    <template #header>
      <h2 v-if="customerDetails?.isDisabled" class="global-h2">Your subscription has ended</h2>
      <h2 v-else class="global-h2">You've used your paid creator visits</h2>
    </template>
    <template #content>
      <b>But we'll be happy to help!</b>
      <div style="margin-top: 7px">
        <b>Contact <CopyText :text="'collabs@shrpa.com'" /> to get the content you need.</b>
      </div>
      <!--<div>If you need multiple collaborations, just email us! <CopyText :text="'collabs@shrpa.com'"/> </div>
          <sui-checkbox style="margin-top:25px" label="I agree to these terms" v-model="userAgreesToPurchaseTerms" />
          <div style="margin-top:10px" v-if="isPurchasingNewCollab"><b>Adding...</b></div>
          <div style="color:red; margin-top:10px" v-if="showPurchaseTermsRequired"><b>Please agree to the terms before accepting.</b></div>-->
    </template>
    <template #footer>
      <div style="gap: 12px; display: flex; flex-wrap: wrap">
        <SrpButton
          v-if="collabsRemaining < 1"
          color="orange"
          @click="
            () => {
              showPurchaseModal = false;
              isAddMoreCreatorVisitsModalVisible = true;
            }
          "
        >
          <template #icon><IconEmbedded name="plus_4" :size="20" /></template>
          Add More Creator Visits
        </SrpButton>
        <SrpButton :isDisabled="isPurchasingNewCollab" @click="cancelPurchase()">Ok</SrpButton>
      </div>
    </template>
  </SrpModal>

  <!--Purchase Modal-->
  <SrpModal v-model:isVisible="showDateOverrideModal" :isClosable="true">
    <template #header>
      <h2 class="global-h2">Simulate a date for testing purposes</h2>
    </template>
    <template #content>
      <h6 class="global-h6" style="margin-bottom: 9px">Pick Simulated Date</h6>
      <VueDatePicker
        class="date-range-picker"
        style="max-width: 300px; margin-bottom: 8px"
        type="date"
        format="MMM d yyyy"
        v-model="currentDateLocal"
        auto-apply
        :enableTimePicker="false"
        :teleportCenter="['mobile'].includes(screenSize)"
      />
      {{ currentDateLocal }}
    </template>
    <template #footer>
      <SrpButton @click="showDateOverrideModal = false" color="gray" fill="outlined">Close</SrpButton>
    </template>
  </SrpModal>

  <!-- Destination contact (customer Creator Visits only) -->
  <SrpModal v-model:isVisible="isDestinationContactModalVisible" size="large" :isCloseButtonVisible="!isSavingDestinationContact" :isClosable="false">
    <template #content>
      <ContactsSlide ref="destinationContactsSlideRef" v-model:destinationContactName="destinationContactDraftName" v-model:destinationPhoneNumber="destinationContactDraftPhone" />
    </template>
    <template #footer>
      <SrpButton @click="closeDestinationContactModal" color="gray" fill="outlined" style="margin-right: 10px" :isDisabled="isSavingDestinationContact">Cancel</SrpButton>
      <SrpButton @click="saveDestinationContact" :isDisabled="isSavingDestinationContact">
        <template v-if="isSavingDestinationContact" #icon><LoadingIcon :size="16" /></template>
        Save
      </SrpButton>
    </template>
  </SrpModal>
</template>

<script lang="ts">
import AdminContext from "@logic/AdminContext";
import axios from "axios";
import CollabStatuses from "@logic/CollabStatuses";
import { defineComponent, inject } from "vue";
import { RouteHelper } from "@helpers/RouteHelper";
import "@vuepic/vue-datepicker/dist/main.css";
import { MetricSender } from "@/helpers/MetricSender";
import { useHead } from "@unhead/vue";
import PlannedCollabsRepo from "@repos/PlannedCollabsRepo";
import CollabsRepo from "@repos/CollabsRepo";
import CustomerDetailsRepo from "@repos/CustomerDetailsRepo";
import { getCollabNameWithFallback } from "@helpers/CollabHelper";

// Types
import { CollabInput, CollabFeedback, CustomerCollabRemainingCounts, CollabAssignedCreator, CreatorCollabSummary, CreatorCollabMatch } from "@contracts/collab";
import { Page } from "@contracts/pages";
import { ScreenSize } from "@contracts/screenSize";
import { PlannedCollab } from "@contracts/plannedCollabs";

// Components
import CollabContentUsage from "./CollabContentUsage.vue";
import CollabFeedbackComponent from "./CollabFeedback.vue";
import CollabSummary from "./CollabSummary/index.vue";
import CommunityCorrections from "./CommunityCorrections/index.vue";
import CopyText from "@components/CopyText.vue";
import CreateCollabModal from "./CreateCollab/index.vue";
import CreatorInvites from "./CreatorInvites.vue";
import Loader from "@components/Loader/Loader.vue";
import Preview from "@components/Preview.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import SrpDetailsSummary from "@components/ui/SrpDetailsSummary.vue";
import SrpFileThumbnail from "@components/ui/SrpFileThumbnail.vue";
import SrpModal from "@components/ui/SrpModal.vue";
import VideoIntroSnippet from "@components/VideoIntroSnippet.vue";
import VueDatePicker from "@vuepic/vue-datepicker";
import AddMoreCreatorVisitsModal from "@components/Modals/AddMoreCreatorVisitsModal.vue";
import PlannedCollabsList from "./PlannedCollabs/PlannedCollabsList.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import SuperUserActionPanel from "@components/SuperUserActionPanel.vue";
import MessagingImpersonateCheckbox from "@/components/MessagingImpersonateCheckbox.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import CommunityBoundaryGuidanceReadonly from "@views/Creator/CollabOpportunity/CollabOpportunityV2/CommunityBoundaryGuidanceReadonly.vue";
import ContactsSlide from "./ChooseCreatorModal/ContactsSlide.vue";

// Stores
import CollabSummariesRepo from "@repos/CollabSummariesRepo";
import LoadingIcon from "@components/ui/LoadingIcon.vue";

export default defineComponent({
  name: "PaidCollabCommunityAdmin",

  components: {
    LoadingIcon,
    AddMoreCreatorVisitsModal,
    IconEmbedded,
    CollabContentUsage,
    CollabFeedbackComponent,
    CollabSummary,
    CommunityCorrections,
    ContactsSlide,
    CopyText,
    CreateCollabModal,
    CreatorInvites,
    Loader,
    PlannedCollabsList,
    Preview,
    SrpButton,
    SrpDetailsSummary,
    SrpFileThumbnail,
    SrpModal,
    VideoIntroSnippet,
    VueDatePicker,
    SuperUserActionPanel,
    MessagingImpersonateCheckbox,
    LinkWithIcon,
    CommunityBoundaryGuidanceReadonly,
  },

  data() {
    const plannedCollabForCreateModal: PlannedCollab | null = null;
    // Defaulting loading to true, otherwise the component shows for a brief moment, disappears while loading, and then reappears
    const plannedCollabsState: { loading: boolean; plannedCollabs: PlannedCollab[] } = { loading: true, plannedCollabs: [] };
    return {
      globalRemoteLogger: inject("globalRemoteLogger") as any,
      globalGetLink: inject("globalGetLink") as any,

      screenSize: inject("screenSize") as ScreenSize,

      title: "Creator Visits",
      // @ts-ignore
      contentBaseUri: globalThis.Bootstrap.Config.contentCdnBaseUri,
      communityId: null,
      collabs: null as Array<CollabInput>,
      customerDetails: null as Page,
      collabRemainingCounts: null as CustomerCollabRemainingCounts | null,
      communityCreatorMatchSummary: null,
      selectedCollabIndex: 0,
      isLoading: true,
      reloadCollabCreatorInputs: 0, // A watch to force a reload of the collab creator inputs
      showCreateOrEditModal: false,
      // Defaulted if the create or edit button is clicked
      collabInputForCreateOrEditFlow: null as CollabInput | null,
      plannedCollabForCreateModal,
      showDeleteModal: false,
      showDateOverrideModal: false,
      currentDateLocal: new Date(), // Used for testing
      showPurchaseModal: false,
      userAgreesToPurchaseTerms: false,
      showPurchaseTermsRequired: false,
      isPurchasingNewCollab: false,
      plannedCollabsState,

      isAddMoreCreatorVisitsModalVisible: false,

      collabSuggestionId: null as string | null,
      defaultTheme: null as string | null,
      version: 3, // Note: Just used for creating collabs, otherwise collab.version is used
      creatorDetails: null as CollabAssignedCreator | null,

      isLoadingCollabSummaries: false as boolean,
      collabSummaries: [] as Array<CreatorCollabSummary>,

      createdForCreatorId: null as string | null,

      isDeletingCollab: false as boolean,

      isImpersonating: false as boolean,

      forceContentUsageVisible: false as boolean,
      enableGenAdventures: false as boolean,

      // This is used to pass between the CreatorInvites component and the CollabSummary component
      invitedCreatorsForCurrentCollab: null as Array<CreatorCollabMatch> | null,

      isDestinationContactModalVisible: false,
      destinationContactDraftName: null as string | null,
      destinationContactDraftPhone: null as string | null,
      isSavingDestinationContact: false,
    };
  },

  computed: {
    isCollabContentUsageVisible(): boolean {
      if (this.forceContentUsageVisible) return true;
      return !!this.selectedCollab?.collaboration?.completedDate && !!this.selectedCollab?.collaboration?.customerOwnedAdventures;
    },
    isCreatorInvitesVisible(): boolean {
      if (this.selectedCollab?.createdForCreatorId) return false;

      let status = this.selectedCollab?.collaboration?.status;
      // Note: We now show this even after they choose a creator (they can save creators now)
      return status && +status >= +CollabStatuses.CreatorsBeingSelectedStatusId;
    },
    hasACollabSubscription(): boolean {
      if (this.customerDetails === null || this.customerDetails.totalPaidCollabs === 0) {
        // No paid collabs
        return false;
      }
      return true;
    },
    selectedCollab: {
      get(): CollabInput | null {
        return this.collabs && this.collabs.length > 0 ? this.collabs[this.selectedCollabIndex] : null;
      },
      set(value: CollabInput) {
        if (!this.collabs) return;
        this.collabs[this.selectedCollabIndex] = value;
      },
    },
    canEditCollabInputs(): boolean {
      var collab = this.collabs[this.selectedCollabIndex];
      // If V1 always return true
      if (collab.version === 1) return false;
      // They can edit until a creator has accepted an invite
      return +collab.collaboration.status < +CollabStatuses.CreatorsAcceptedStatusId;
    },
    collabsRemaining(): number {
      if (!this.customerDetails) return -1;
      // Update Nov 2024: We now let the customer create as many collabs as they want but only post to creators
      // as many as they have paid for.
      const postedOrCompletedCollabs = this.collabs.filter(c => c.collaboration.completedDate || c.readyToPost).length;
      const remaining = this.customerDetails.totalPaidCollabs - postedOrCompletedCollabs;
      return remaining > 0 ? remaining : 0;
    },
    collabsTerm(): string {
      if (this.customerDetails && this.customerDetails.totalPaidCollabs - this.collabs.length === 1) return "Creator Visit";
      else return "Creator Visits";
    },
  },

  watch: {
    selectedCollabIndex: {
      handler(newVal, oldVal) {
        this.collabSelectionChanged(newVal);
        if (this.isDestinationContactModalVisible) {
          this.isDestinationContactModalVisible = false;
        }
      },
    },
  },

  created() {
    this.globalRoot.allCollabsComponent = this;
  },

  async mounted() {
    useHead({ title: () => this.title ?? "" });

    await this.checkForCollabSuggestionId();

    const versionQuery = RouteHelper.getQueryStringParam("version");
    if (versionQuery) {
      this.version = +versionQuery;
    }

    const id = this.$route.params.communityId as string;
    await AdminContext.setOrgInContextFromCustomerId(id);
    await this.loadPlannedCollabs(id); // Moved this up to stop the page from jumping when it loads in.
    await this.loadCollabs(id);
    await this.loadCustomerDetails(id);

    this.title += ` - ${this.customerDetails.title}`;

    this.handleCreatedForCreatorIdFlow();
    this.handleOpenNewCollabModalHash();

    this.isLoading = false; // We want both collabs and customer subscription details to load before we render
    await this.loadCommunityCreatorMatchSummary(id);

    MetricSender.sendMetric("Cust-ViewCollabs", null);

    this.isLoadingCollabSummaries = true;
    this.collabSummaries = await CollabSummariesRepo.loadAllCollabSummaries(id, false);
    this.isLoadingCollabSummaries = false;
  },

  methods: {
    handleOpenNewCollabModalHash() {
      if (RouteHelper.getHashParam("openNewCollabModal")) {
        // removes the "openNewCollabModal" hash value to prevent the creation of another identical Collab on reload
        const hashParams = new URLSearchParams(this.$route.hash.substring(1));
        hashParams.delete("openNewCollabModal");
        this.$router.replace({ ...this.$route, hash: `#${hashParams.toString()}` });

        this.newCollabClicked();
      }
    },
    handleCreatedForCreatorIdFlow() {
      if (RouteHelper.getHashParam("createdForCreatorId")) {
        this.createdForCreatorId = RouteHelper.getHashParam("createdForCreatorId");

        // removes the "createdForCreatorId" hash value to prevent the creation of another identical Collab on reload
        const hashParams = new URLSearchParams(this.$route.hash.substring(1));
        hashParams.delete("createdForCreatorId");
        this.$router.replace({ ...this.$route, hash: `#${hashParams.toString()}` });

        this.newCollabClicked();
      }
    },
    async loadCustomerDetails(identifier: string) {
      const customerDetailsFull = await CustomerDetailsRepo.loadCustomerDetails(identifier, false);
      this.customerDetails = customerDetailsFull.page;
    },
    async checkForCollabSuggestionId() {
      const collabSuggestionId = RouteHelper.getHashParam("collabSuggestionId");
      if (collabSuggestionId) {
        this.collabSuggestionId = collabSuggestionId;
        this.newCollabClicked();
      }
    },
    async loadCollabs(identifier: string) {
      this.collabs = await CollabsRepo.loadCustomerCollabs(identifier);
      // Setting the Id after collabs are set since we use it in v-if statements
      this.communityId = identifier;
      this.defaultCollab();
      this.defaultFeedback();
      this.loadCollabRemainingCounts(this.selectedCollab?.id);
    },
    defaultCollab() {
      const collabInputId = RouteHelper.getQueryStringOrHashParam("collab");
      if (collabInputId) {
        var index = this.collabs.findIndex(c => c.id === collabInputId);
        if (index >= 0) {
          this.selectedCollabIndex = index;
        }
      }
    },
    defaultFeedback() {
      // If it's completed but has not feedback, default the feedback which shows the section
      if (this.collabs[this.selectedCollabIndex] && this.collabs[this.selectedCollabIndex].collaboration.completedDate && !this.collabs[this.selectedCollabIndex].feedback) {
        // this.globalLog.info("Defaulting feedback for collab " + this.collabs[this.selectedCollabIndex].id);
        this.collabs[this.selectedCollabIndex].feedback = {
          photoRating: 0,
          creatorRating: 0,
          overallRating: 0,
          feedbackForCreator: null,
          feedbackForShrpa: null,
          submittedDate: null,
        } as CollabFeedback;
      }
    },
    async loadCollabRemainingCounts(collabInputIdInContext: string | null) {
      let uri = `${import.meta.env.VITE_API_URL}/collabs/${this.communityId}/remaining-counts`;
      if (collabInputIdInContext) uri += `?collabInputIdInContext=${collabInputIdInContext}`;
      const response = await axios.get(uri);
      this.collabRemainingCounts = response.data;
      // this.globalLog.info(`loadCollabRemainingCounts: ${JSON.stringify(this.collabRemainingCounts)}`);
    },
    async loadCommunityCreatorMatchSummary(identifier) {
      const { data } = await axios.get(`${import.meta.env.VITE_API_URL}/community/${identifier}/creator-match-summary?creatorExampleCount=3`);
      this.communityCreatorMatchSummary = data;
    },
    // Event from the CreateCollab wizard that a collab was created
    collabCreated(newCollab: CollabInput) {
      this.collabInputForCreateOrEditFlow = newCollab;
      // Add to the front of the array
      this.collabs.unshift(newCollab);
      this.selectedCollabIndex = 0;
      this.collabSelectionChanged(this.selectedCollabIndex);

      this.loadCollabRemainingCounts(newCollab.id);
      this.updateSelectedPlannedCollab(newCollab.id);
    },
    collabUpdate(collab: CollabInput, index: number) {
      // Need to use this or Vue won't see the change
      this.collabs[index] = collab;
      if (collab.collaboration && collab.collaboration.completedDate) {
        // If the collab just completed we should default feedback so that section shows
        this.defaultFeedback();
      }
    },
    feedbackUpdate(feedback: CollabFeedback) {
      this.collabs[this.selectedCollabIndex].feedback = feedback;
    },
    cancelCreate() {
      this.plannedCollabForCreateModal = null;
      this.collabSuggestionId = null;
      this.showCreateOrEditModal = false;
      this.collabInputForCreateOrEditFlow = null;
      this.createdForCreatorId = null;
    },
    newCollabClicked() {
      this.collabInputForCreateOrEditFlow = {
        // Just the fields on the first panel, after that we'll populate this from the server
        name: "",
        preferredDateRangeStart: null,
        preferredDateRangeEnd: null,
        preferredTiming: null,
        reminderForFarOutRequested: false,
      } as CollabInput;

      this.showCreateOrEditModal = true;
    },
    editCurrentCollab() {
      this.collabInputForCreateOrEditFlow = this.collabs[this.selectedCollabIndex];
      this.showCreateOrEditModal = true;
    },
    openDestinationContactModal() {
      const c = this.selectedCollab;
      if (!c?.id || !this.communityId) return;
      this.destinationContactDraftName = c.destinationContactName;
      this.destinationContactDraftPhone = c.destinationPhoneNumber;
      this.isDestinationContactModalVisible = true;
    },
    closeDestinationContactModal() {
      if (this.isSavingDestinationContact) return;
      this.isDestinationContactModalVisible = false;
    },
    async saveDestinationContact() {
      const slide = this.$refs.destinationContactsSlideRef as { showWarning?: () => void } | undefined;
      const nameTrimmed = this.destinationContactDraftName?.trim() ?? "";
      const phoneTrimmed = this.destinationContactDraftPhone?.trim() ?? "";
      if (!nameTrimmed || !phoneTrimmed) {
        slide?.showWarning?.();
        return;
      }
      if (!this.communityId || !this.selectedCollab?.id) return;
      this.isSavingDestinationContact = true;
      try {
        const updated = await CollabsRepo.updateDestinationContact(this.communityId, this.selectedCollab.id, {
          destinationContactName: nameTrimmed,
          destinationPhoneNumber: phoneTrimmed,
        });
        Object.assign(this.collabs![this.selectedCollabIndex], updated);
        this.isDestinationContactModalVisible = false;
      } finally {
        this.isSavingDestinationContact = false;
      }
    },
    async refreshCreatorDetails() {
      // Refresh creator details from server since the create flow may have updated state client-side and then cancelled (ex. createdFor)
      // @ts-ignore
      this.$refs.collabSummary?.load(); // May not be set if no collabs are created yet
    },
    askToBuyCollab(isPurchaseModalSkipped = false) {
      this.globalRemoteLogger.info(`Customer OutOfCollabs: ${this.communityId}`, {
        customerId: this.communityId,
        collabIdInContext: this.selectedCollab?.id,
        collabNameInContext: this.selectedCollab?.name,
      });
      if (isPurchaseModalSkipped) {
        this.isAddMoreCreatorVisitsModalVisible = true;
      } else {
        this.showPurchaseModal = true;
      }
    },
    cancelPurchase() {
      this.showPurchaseModal = false;
    },
    currentCollabDeleted() {
      const indexToDelete = this.selectedCollabIndex;
      if (this.selectedCollabIndex !== 0) {
        this.selectedCollabIndex--;
      }
      this.collabs.splice(indexToDelete, 1);

      this.refreshCreatorDetails();
    },
    getCollabNameText(collab: CollabInput): string {
      var name = getCollabNameWithFallback(collab.name);
      if (collab.collaboration?.completedDate) {
        name = name + " - Completed!";
      } else if (!collab.readyToPost && !collab.postedToCreators) {
        name = name + " - Draft";
      }
      return name;
    },
    collabSelectionChanged(newValue) {
      if (newValue?.target?.value) {
        newValue = newValue?.target?.value;
      }
      if (newValue >= 0) {
        const hashesToKeep = [];
        // Ideally this would be smart enough to just keep the rest of the hash
        if (location.hash?.includes("contactCreator=true")) hashesToKeep.push("contactCreator=true");
        if (location.hash?.includes("messages=true")) hashesToKeep.push("messages=true");

        let newHash = `#collab=${this.collabs[this.selectedCollabIndex].id}`;
        if (hashesToKeep.length > 0) {
          newHash += `&${hashesToKeep.join(String.fromCharCode(38))}`;
        }
        history.replaceState(undefined, undefined, newHash);
      }
      this.currentDateLocal = new Date();
      this.loadCollabRemainingCounts(this.selectedCollab.id);
      this.defaultFeedback();
      // todo: Do an IsDirty check here in case they didn't save
    },
    // collabHasRequiredFieldsFilled(collab: CollabInput) {},
    askToDelete() {
      this.showDeleteModal = true;
    },
    cancelDelete() {
      this.showDeleteModal = false;
    },
    async deleteCollab() {
      this.isDeletingCollab = true;

      var currentCollab = this.collabs[this.selectedCollabIndex];
      const uri = `${import.meta.env.VITE_API_URL}/collabs/${this.communityId}/inputs/${currentCollab.id}`;
      await axios.delete(uri);
      this.currentCollabDeleted();
      this.showDeleteModal = false;

      this.isDeletingCollab = false;
    },
    async loadPlannedCollabs(customerId: string) {
      this.plannedCollabsState.loading = true;
      this.plannedCollabsState.plannedCollabs = await PlannedCollabsRepo.fetchPlannedCollabs(customerId);
      this.plannedCollabsState.loading = false;
    },
    startCollabFromPlannedCollab(plannedCollab: PlannedCollab) {
      this.plannedCollabForCreateModal = plannedCollab;
      this.newCollabClicked();
    },
    async savePlannedCollabs(plannedCollabs: PlannedCollab[]) {
      this.plannedCollabsState.plannedCollabs = await PlannedCollabsRepo.updatePlannedCollabs(this.customerDetails.id, plannedCollabs);
    },

    async updateSelectedPlannedCollab(collabId: string) {
      if (!this.plannedCollabForCreateModal) return;
      const selectedPlannedCollab = this.plannedCollabsState.plannedCollabs.find(plannedCollab => plannedCollab.id === this.plannedCollabForCreateModal.id);
      if (!selectedPlannedCollab) return;

      selectedPlannedCollab.createdCollaborationId = collabId;
      this.savePlannedCollabs(this.plannedCollabsState.plannedCollabs);
    },
    scrollToFeedback() {
      this.$nextTick(() => {
        this.$refs.feedbackSection?.$el?.scrollIntoView({ behavior: "smooth" });
      });
    },
    scrollToContentUsage() {
      setTimeout(() => {
        this.$refs.contentUsageSection?.$el?.scrollIntoView({ behavior: "smooth" });
      }, 2000);
    },
  },
});
</script>

<style scoped lang="scss">
@import "./AllCollabsV2.scss";

// AllCollabs.vue-specific overrides
.header {
  padding-bottom: 0;
  border-bottom: none;

  &__left-side {
    width: calc(100% - 275px);
  }

  &__right-side {
    width: calc($desktop-wide-grid-col-width * 3 + $desktop-wide-grid-gap-width * 2);
    min-width: calc($desktop-wide-grid-col-width * 3 + $desktop-wide-grid-gap-width * 2);
  }
}
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
  .header__right-side {
    width: calc($desktop-grid-col-width * 3 + $desktop-grid-gap-width * 2);
    min-width: calc($desktop-grid-col-width * 3 + $desktop-grid-gap-width * 2);
  }
}
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
  .header__right-side {
    width: calc($laptop-grid-col-width * 3 + $laptop-grid-gap-width * 2);
    min-width: calc($laptop-grid-col-width * 3 + $laptop-grid-gap-width * 2);
  }
}
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
  .header__right-side {
    width: calc($tablet-large-grid-col-width * 3 + $tablet-large-grid-gap-width * 2);
    min-width: calc($tablet-large-grid-col-width * 3 + $tablet-large-grid-gap-width * 2);
  }
}
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
  .header {
    padding-bottom: 22px;
    flex-direction: column-reverse;

    &__left-side {
      width: 100%;
      margin: 0;
    }

    &__right-side {
      width: 100%;
      margin-bottom: 20px;
    }
  }
}
@media (max-width: $mobile-max-width) {
  .header {
    padding-bottom: 18px;
    flex-direction: column-reverse;

    &__left-side {
      width: 100%;
      margin: 0;
    }

    &__right-side {
      width: 100%;
      margin-bottom: 20px;
    }
  }
}

.collab-select-dropdown {
  &__caret-icon {
    width: auto;
    height: auto;
    font-size: unset;
    line-height: unset;
    position: absolute;
    inset: 16px 14px auto auto;

    :deep(svg) {
      fill: rgba(0, 0, 0, 1);
    }
  }

  &__select-itself:focus + &__caret-icon {
    top: 14px;
  }
}

.manage-buttons-panel {
  padding: 0;
  padding-bottom: 15px;

  &__left-side {
    flex-wrap: nowrap;
  }
}
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
  .manage-buttons-panel {
    flex-direction: row;
    align-items: center;

    &__left-side {
      margin-bottom: 0;
    }
  }
}

.creator-visits-page {
  &__h1 {
    margin-bottom: 12px;
    margin-left: -25px;
  }

  &__quick-links {
    display: flex;
    gap: 20px;
    margin-bottom: 17px;
    margin-left: -25px;
  }

  &__collab-select-dropdown {
    margin-bottom: 0;
  }

  &__admin-buttons-section {
    display: flex;
    justify-content: flex-end;
  }
}
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
  .creator-visits-page__h1 {
    margin-left: -25px;
  }
}
@media (max-width: $mobile-max-width) {
  .creator-visits-page {
    &__h1 {
      margin-left: -5px;
      margin-bottom: 12px;
    }

    &__quick-links {
      margin-left: -5px;
      flex-direction: column;
      gap: 12px;
    }

    &__planned-visits-spoiler {
      width: calc(100% + 30px);
      padding: 20px 15px 15px;
      margin-left: -15px;
    }

    &__spoiler-section:last-child {
      margin-bottom: 0;
    }

    &__admin-buttons-section {
      width: calc(100% + 30px);
      padding: 0 15px;
      margin: 0 0 22px -15px;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}
</style>
